<!--
  TaskButton.vue

  This component represents a button to link to another webpage. 

  Props:
    - href: redirect link.
    - imageSrc: Required path to the asset to display.
    - imageAlt: Alt text for image.
    - overrideClass: Additional classes.
    - onClick: Optional function to execute when the button is clicked.
    - enterDown: Optional function to execute when the down key is entered.
    - disabled: Optional boolean to disable the button. Default is false.

  The component uses scoped SCSS for styling. The button's appearance changes when hovered over or disabled.
-->
<template>
  <a :href="href" class="task-button" :class="overrideClass" role="button" :disabled="disabled" @click="onClick" @keydown.enter="enterDown" aria-label="task button">
    <img :src="imageSrc" :alt="imageAlt" class="task-image" role="presentation" />
    <slot></slot>
    <div class="icon-container">
      <svg v-if="hasArrow">
        <use xlink:href="/assets/public/images/icons.svg#arrow" />
      </svg>
    </div>
  </a>
</template>

<script>
export default {
  name: 'TaskButton',
  props: {
    href: {
      type: String,
      required: false,
    },
    imageSrc: {
      type: String,
      required: true,
    },
    imageAlt: {
      type: String,
      required: true,
    },
    overrideClass: {
      type: String,
      required: false,
      default: '',
    },
    hasArrow: {
      type: Boolean,
      required: false,
      default: () => {},
    },
    onClick: {
      type: Function,
      required: false,
      default: () => {},
    },
    enterDown: {
      type: Function,
      required: false,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  methods: {
    handleClick() {
      this.$emit('click');
    },
  },
};
</script>

<style lang="scss" scoped>
.task-button {
  box-sizing: border-box;
  display: flex !important;
  flex-direction: row;
  align-items: center;
  column-gap: 1.5rem;
  padding: 1rem;
  border: 1px solid $GREY_200;
  border-radius: 4px;
  width: 100%;
  min-width: rem(200);
  font-weight: $BOLD;
  color: $GREY_900;
  text-decoration: none;
  margin-bottom: 0.5rem;
  background-color: $WHITE;

  &:hover {
    box-shadow: 0px 2px 6px 0px rgba($GREY_800, 0.06), 0px 2px 4px 0px rgba($GREY_800, 0.02);
    font-weight: $NORMAL;
    text-decoration: underline;
    color: $GREY_900;
  }

  .task-image {
    height: 3rem;
    width: 3rem;
    padding: 0.5rem;
  }

  .icon-container {
    height: 1rem;
    width: 1rem;
    min-width: 1rem;
    margin-left: auto;
    color: $GREY_700;
  }
}
</style>
